import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import { ChangeDetectorRef, Component, Inject, OnDestroy } from '@angular/core';
import { FieldTypeConfig } from '@ngx-formly/core';
import { AnalyticsAwareFieldType } from '../analytics-aware-field-type';
import { Subject, Subscription } from 'rxjs';
import { ToastService } from '../../../services/toast/toast.service';
import { IIremboFileUpload } from '../../../models/irembo-file-upload-response.model';
import { DomSanitizer } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { IPreviousAttachmentEvent } from '../../../models/previous-attachment-event';
import { FileUploadSource } from '../../../models/file-upload-source.enum';
import { ICertificateAttachmentEvent } from '../../../models/certificate-attachment-event';
import { IEnvironment } from '../../../models/environment.model';
import { UserDocumentType } from '../../../models/user-document-type.enum';
import { getCertificateNameByLocale } from '../../../../utils/utils/application-form.util';
import { formatFileSizeBytesUtil } from '../../../../utils/utils/base-64-uri-to-file-converter.util';
import { FormStateService } from '.././../../services/formly/form-state.service';
import { ValidationErrors } from '@angular/forms';

const FIND_USER_CERTIFICATES_API =
  '/application/v1/application/find-user-certificates';
const FIND_USER_PREVIOUS_ATTACHMENTS_API =
  '/application/v1/application/find-user-previous-attachments';
const FILEMANAGER_URL = '/application/v1/file-manager/application/attachment';
const CERTIFICATE_URL = '/document-generation/v1/file-manager/certificate';
const PHOTO_VALIDATION_URL = '/photo-validation/api/photo/validate';

@Component({
  selector: 'irembogov-custom-file-upload',
  templateUrl: './custom-file-upload.component.html',
})
export class CustomFileUploadComponent
  extends AnalyticsAwareFieldType<FieldTypeConfig>
  implements OnDestroy
{
  private subscriptions = new Subscription();

  noFileActionLabel = 'Click to upload';
  noFileSubLabelHtml = 'or drag and drop';
  maxSizeText = 'max.';

  totalPreviousAttachments = 0;
  totalCertificateAttachments = 0;
  previousAttachments: IPreviousAttachmentEvent[] = [];
  certificateAttachments: ICertificateAttachmentEvent[] = [];
  filePath: string | undefined;
  loadingFile = false;
  progress = new Subject<number>();
  allowUploadPreviousFiles = true;

  page = 0;
  size = 10;
  sort = 'dateCreated,asc';
  placeholder = 'Select File to upload';
  dataField = 'data.content';

  file: IIremboFileUpload | null | undefined;
  private environment: IEnvironment;
  photoValidationResponse: Record<string, unknown> | undefined;
  validatingPhoto = false;
  duplicateFile = false;

  // Virus scanning feature flag
  virusScanEnabled = true;

  invalidPhotoDimensions = false;
  photoRequiredDimensions = '';
  photoActualDimensions = '';

  constructor(
    private cd: ChangeDetectorRef,
    private http: HttpClient,
    private toastService: ToastService,
    private sanitizer: DomSanitizer,
    public modalService: NgbModal,
    @Inject('environment') environment: IEnvironment,
    private formStateService: FormStateService
  ) {
    super();
    this.environment = environment;
    this.allowUploadPreviousFiles =
      this.environment.allowUploadPreviousFiles ?? true;
  }

  private updateNoFileSubLabelHtml(): void {
    this.noFileSubLabelHtml = 'or drag and drop <br />';
    if (
      !(
        this.field.props['allowedFormats'] ||
        this.field.props['maximumUploadSize'] ||
        this.field.props['maximumUploadSize']
      )
    ) {
      return;
    }
    let fileFormats = '';
    if (
      this.field.props['allowedFormats'] &&
      Array.isArray(this.field.props['allowedFormats'])
    ) {
      fileFormats = this.field.props['allowedFormats'].join(', ').toUpperCase();
    }

    const sizeLimits: string[] = [];

    this.calculateSizeLimit(
      this.field.props['maximumUploadSize'],
      sizeLimits,
      'max. '
    );

    this.calculateSizeLimit(
      this.field.props['minimumUploadSize'],
      sizeLimits,
      'min. '
    );
    this.noFileSubLabelHtml = `or drag and drop <br />${fileFormats}${
      sizeLimits.length > 0 ? '(' + sizeLimits.join(',') + ')' : ''
    }`;
    this.cd.detectChanges();
  }

  private calculateSizeLimit(
    size: string | undefined,
    sizeLimits: string[],
    prepend?: string
  ): void {
    if (size && !Number.isNaN(+size)) {
      sizeLimits.push(prepend + formatFileSizeBytesUtil(+size * 1024, 0));
    }
  }

  async onFileUploadEvent(file: IIremboFileUpload) {
    const isImage = !!file?.fileType && file.fileType.includes('image');
    this.photoValidationResponse = {};
    this.invalidPhotoDimensions = false;
    this.photoRequiredDimensions = '';
    this.photoActualDimensions = '';

    // Validate photo dimensions if configured
    if (
      this.field.props['validatePhotoDimensions'] &&
      isImage &&
      file['content'] &&
      typeof file['content'] === 'string'
    ) {
      const dimensionValidation = await this.validateImageDimensions(
        file,
        this.field.props['validatePhotoDimensions']
      );

      if (dimensionValidation.valid) {
        const currentErrors = { ...(this.field.formControl.errors || {}) };
        delete currentErrors['invalidDimensions'];
        const newErrors = Object.keys(currentErrors).length
          ? currentErrors
          : null;
        this.field.formControl.setErrors(newErrors);
      } else {
        this.invalidPhotoDimensions = true;
        this.photoRequiredDimensions = dimensionValidation.required || '';
        this.photoActualDimensions = dimensionValidation.actual || '';
        this.field.formControl.setErrors({
          ...(this.field.formControl.errors || {}),
          invalidDimensions: true,
        });
        this.cd.detectChanges();
        return;
      }
    }

    if (this.field.props['validatePhoto'] && file['content'] && isImage) {
      const fileContent = file.content as string;
      const base64Str = fileContent.replace(/^data:image\/[a-z]+;base64,/, '');
      this.validatingPhoto = true;
      this.validatePhoto(base64Str).subscribe({
        next: res => {
          const checks = res['checks'] as Record<string, string>;
          this.photoValidationResponse = { ...checks, has_checks: true };
          this.validatingPhoto = false;
          this.cd.detectChanges();
        },
        error: () => {
          this.photoValidationResponse = { has_checks: false };
          this.validatingPhoto = false;
          this.cd.detectChanges();
        },
      });
    }
    this.cd.detectChanges();
  }

  private validateImageDimensions(
    file: IIremboFileUpload,
    requiredDimensions: { width: number; height: number }
  ): Promise<{
    valid: boolean;
    required?: string;
    actual?: string;
    isError?: boolean;
  }> {
    // Check if file is an image
    if (!file.fileType.includes('image')) {
      return Promise.resolve({ valid: true });
    }

    const requiredWidth = requiredDimensions.width;
    const requiredHeight = requiredDimensions.height;

    // Validate that the dimensions are valid numbers
    if (
      !requiredWidth ||
      !requiredHeight ||
      requiredWidth <= 0 ||
      requiredHeight <= 0
    ) {
      this.toastService.show({
        body: 'Invalid photo dimension configuration',
        type: 'error',
        delay: 5000,
      });
      return Promise.resolve({ valid: false, isError: true });
    }

    return new Promise(resolve => {
      const img = new Image();

      img.onload = () => {
        const actualWidth = img.width;
        const actualHeight = img.height;

        if (actualWidth !== requiredWidth || actualHeight !== requiredHeight) {
          resolve({
            valid: false,
            required: `${requiredWidth} x ${requiredHeight}`,
            actual: `${actualWidth} x ${actualHeight}`,
          });
        } else {
          resolve({ valid: true });
        }
      };

      img.onerror = () => {
        resolve({
          valid: false,
          isError: true,
        });
      };

      img.src = file.content as string;
    });
  }

  /**
   * Handle validation errors from the file upload component,
   * including virus scan errors
   */
  handleFileValidationErrors(errors: ValidationErrors) {
    if (errors['virusDetected']) {
      this.toastService.show({
        body: `Virus detected in the file. Please select a different file.`,
        type: 'error',
        delay: 10000,
      });
    } else if (errors['virusScanError']) {
      this.toastService.show({
        body: `Error scanning file for viruses. Please try again or contact support.`,
        type: 'error',
        delay: 5000,
      });
    }
    this.cd.detectChanges();
  }

  loadUserDocuments(endpoint: string, type: UserDocumentType) {
    const url = `${this.environment.apiGatewayBaseUrl}${endpoint}`;
    const { dataset } = this.field.props;
    const params: Record<string, unknown> = {
      page: this.page,
      size: this.size,
      sort: this.sort,
    };

    const keyString = dataset?.['dataField']
      ? dataset['dataField']
      : this.dataField;
    if (keyString) {
      const queryParams = Object.keys(params)
        .map(key => `${key}=${params[key]}`)
        .join('&');

      this.subscriptions.add(
        this.http
          .get<Record<string, unknown>>(`${url}?${queryParams}`)
          .subscribe({
            next: (res: Record<string, unknown>) => {
              const data = res['data'] as Record<string, unknown>;
              const totalElements = data['totalElements'] as number;
              this.loadCertificates(type, res, keyString, totalElements);
              this.loadPreviousDocuments(type, res, keyString, totalElements);
              this.cd.detectChanges();
            },
            error: () => {
              this.toastService.show({
                body: `Error loading ${type}`,
                type: 'error',
              });
              this.cd.detectChanges();
            },
          })
      );
    }
  }

  private loadCertificates(
    type: UserDocumentType,
    res: Record<string, unknown>,
    keyString: string,
    totalElements: number
  ) {
    if (type === UserDocumentType.CERTIFICATES) {
      this.totalCertificateAttachments = totalElements;
      const certificateAttachmentsData = Array.isArray(res)
        ? (res as ICertificateAttachmentEvent[])
        : this.getValueFromNestedObject<ICertificateAttachmentEvent>(
            res,
            keyString
          );
      this.certificateAttachments.push(...certificateAttachmentsData);
      // Construct the display name for the certificate attachments
      const locale = localStorage.getItem('locale') ?? 'en-US';
      this.certificateAttachments.forEach(attachment => {
        const serviceName = attachment.serviceName;
        const applicationNumber = attachment.certificateNames[locale]
          .split('-')
          .pop();
        attachment.displayName = `${serviceName} - ${applicationNumber}`;
      });
    }
  }

  private loadPreviousDocuments(
    type: UserDocumentType,
    res: Record<string, unknown>,
    keyString: string,
    totalElements: number
  ) {
    if (type === UserDocumentType.PREVIOUS_ATTACHMENTS) {
      this.totalPreviousAttachments = totalElements;
      const previousAttachmentsData = Array.isArray(res)
        ? (res as IPreviousAttachmentEvent[])
        : this.getValueFromNestedObject<IPreviousAttachmentEvent>(
            res,
            keyString
          );
      this.previousAttachments.push(...previousAttachmentsData);
      // Construct the display name for the previous attachments
      this.previousAttachments.forEach(attachment => {
        const fileName = attachment.fileName;
        const applicationNumber = fileName.split('_')[0];
        const originalName = attachment.fileOriginalName;
        attachment.displayName = `${applicationNumber} - ${originalName}`;
      });
    }
  }

  onSelectFile() {
    if (this.previousAttachments.length === 0) {
      const { dataset } = this.field.props;
      if (dataset?.['params']) {
        const externalParams = dataset['params'] as Record<string, unknown>;
        if (
          externalParams['size'] &&
          typeof externalParams['size'] === 'number'
        ) {
          this.size = externalParams['size'];
        }
        if (
          externalParams['sort'] &&
          typeof externalParams['sort'] === 'string'
        ) {
          this.sort = externalParams['sort'];
        }
      }

      this.loadUserDocuments(
        FIND_USER_CERTIFICATES_API,
        UserDocumentType.CERTIFICATES
      );
      this.loadUserDocuments(
        FIND_USER_PREVIOUS_ATTACHMENTS_API,
        UserDocumentType.PREVIOUS_ATTACHMENTS
      );
    }
  }

  private getValueFromNestedObject<T>(
    obj: Record<string, unknown>,
    keyString: string
  ): T[] {
    const keys = keyString.split('.');
    let value = obj;
    for (const key of keys) {
      value = value[key] as Record<string, unknown>;
      if (value === undefined) {
        throw new Error(`Invalid key string: ${keyString}`);
      }
    }
    return value as unknown as T[];
  }

  handlePreviousAttachmentPagination(event: Record<string, number>) {
    this.handlePagination(event, UserDocumentType.PREVIOUS_ATTACHMENTS);
  }

  handleCertificateAttachmentPagination(event: Record<string, number>) {
    this.handlePagination(event, UserDocumentType.CERTIFICATES);
  }

  handlePagination(event: Record<string, number>, type: UserDocumentType) {
    const limit = event['scrollHeight'] - event['clientHeight'];
    const moreItems =
      type === UserDocumentType.CERTIFICATES
        ? this.totalCertificateAttachments !==
          this.certificateAttachments.length
        : this.totalPreviousAttachments !== this.previousAttachments.length;

    if (event['scrollPosition'] === limit && moreItems) {
      this.page++;
      if (type === UserDocumentType.PREVIOUS_ATTACHMENTS) {
        this.loadUserDocuments(
          FIND_USER_PREVIOUS_ATTACHMENTS_API,
          UserDocumentType.PREVIOUS_ATTACHMENTS
        );
      }
      if (type === UserDocumentType.CERTIFICATES) {
        this.loadUserDocuments(
          FIND_USER_CERTIFICATES_API,
          UserDocumentType.CERTIFICATES
        );
      }
    }
  }

  selectPreviousAttachmentEvent(event: IPreviousAttachmentEvent) {
    const file_manager_url = `${this.environment.apiGatewayBaseUrl}${FILEMANAGER_URL}`;

    this.modalService.dismissAll();
    this.loadingFile = true;
    this.cd.detectChanges();
    this.subscriptions.add(
      this.getBlob(
        { id: event.applicationId, fileName: event.fileName },
        file_manager_url
      ).subscribe({
        next: (httpEvent: HttpEvent<Blob>) => {
          this.updateControlValue(
            httpEvent,
            event.fileName,
            FileUploadSource.PREVIOUS_ATTACHMENT,
            this.field.props['attachmentCode'],
            this.field.props['isCertificate']
          );
          this.loadingFile = false;
        },
        error: (error: any) => {
          this.loadingFile = false;
          const displayedErrorMessage = 'Error selecting previous attachments';
          this.toastService.show({
            body: displayedErrorMessage,
            type: 'error',
          });
          this.cd.detectChanges();
          this.emitFetchOutcome('failure', error, displayedErrorMessage);
        },
      })
    );
  }

  selectCertificateAttachmentEvent(event: ICertificateAttachmentEvent) {
    const file_manager_url = `${this.environment.apiGatewayBaseUrl}${CERTIFICATE_URL}`;

    this.modalService.dismissAll();
    this.loadingFile = true;
    this.cd.detectChanges();
    this.subscriptions.add(
      this.getBlob(
        { certificateName: getCertificateNameByLocale(event.certificateNames) },
        file_manager_url
      ).subscribe({
        next: (httpEvent: HttpEvent<Blob>) => {
          this.updateControlValue(
            httpEvent,
            getCertificateNameByLocale(event.certificateNames),
            FileUploadSource.CERTIFICATE,
            this.field.props['attachmentCode'],
            this.field.props['isCertificate']
          );
          this.loadingFile = false;
        },
        error: (error: any) => {
          this.loadingFile = false;
          const displayedErrorMessage =
            'Error selecting certificate attachments';
          this.toastService.show({
            body: displayedErrorMessage,
            type: 'error',
          });
          this.cd.detectChanges();
          this.emitFetchOutcome('failure', error, displayedErrorMessage);
        },
      })
    );
  }

  private updateControlValue(
    httpEvent: HttpEvent<Blob>,
    fileName: string,
    source: FileUploadSource,
    attachmentCode: string | undefined,
    isCertificate: string | undefined
  ) {
    if (httpEvent.type === HttpEventType.DownloadProgress) {
      if (httpEvent.total !== undefined) {
        const progressPercentage = Math.round(
          (100 * httpEvent.loaded) / httpEvent.total
        );
        this.progress.next(progressPercentage);
      }
    } else if (httpEvent.type === HttpEventType.Response) {
      const blob = httpEvent.body;
      if (!blob) {
        this.emitFetchOutcome('failure', { code: 'EMPTY_FILE_BODY' });
        return;
      }
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onerror = () => {
        this.loadingFile = false;
        this.emitFetchOutcome('failure', { code: 'FILE_READ_ERROR' });
      };
      reader.onload = async () => {
        this.loadingFile = false;
        // Create a fresh file object
        const newFile: IIremboFileUpload = {
          fileName,
          fileSize: blob.size,
          fileType: blob.type,
          fileDownloadName: '',
          content: reader.result as string,
          source,
          attachmentCode,
          isCertificate,
        };
        // Assign it to this.file so Angular sees a new reference
        this.file = { ...newFile };
        // Update the form control
        this.field.formControl.setValue(this.file);
        this.cd.detectChanges();
        this.filePath = this.sanitizer.bypassSecurityTrustResourceUrl(
          reader.result as string
        ) as string;
        // Compute SHA-256 hash of file content
        const fileHash = await this.formStateService.computeSHA256(
          reader.result as string
        );
        // Check if file content is already in uploadedFiles
        if (this.formStateService.isDuplicateFile(fileHash)) {
          this.duplicateFile = true;
          this.cd.markForCheck();
          return;
        }
        this.formStateService.saveHashAttachmentInArray(fileName, fileHash);
        this.field.formControl.setValue({
          fileName,
          fileSize: blob.size,
          fileType: blob.type,
          fileDownloadName: '',
          content: reader.result,
          source,
          attachmentCode,
          isCertificate,
        });
        // Emit only on the committed, non-duplicate attach (the duplicate path
        // returns early above).
        this.emitFetchOutcome('success');
      };
    }
  }

  private getBlob(
    queryParams: Record<string, unknown>,
    fileManagerUrl: string
  ) {
    const params = Object.keys(queryParams)
      .map(key => `${key}=${encodeURIComponent(queryParams[key] as string)}`)
      .join('&');
    return this.http.get(`${fileManagerUrl}?${params}`, {
      responseType: 'blob',
      reportProgress: true,
      observe: 'events',
    });
  }

  private validatePhoto(data: string) {
    const url = `${this.environment.apiGatewayBaseUrl}${PHOTO_VALIDATION_URL}`;
    return this.http.post<Record<string, unknown>>(url, { data });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
